import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { BarChart3, TrendingUp, Trophy } from 'lucide-react';

interface PollProps {
  contentId: string;
  question: string;
  options: string[];
}

export const InteractivePoll: React.FC<PollProps> = ({ contentId, question, options }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [responses, setResponses] = useState<{ [key: string]: number }>({});
  const [userVote, setUserVote] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadResponses();
  }, [contentId]);

  const loadResponses = async () => {
    const { data } = await supabase
      .from('course_content')
      .select('metadata')
      .eq('id', contentId)
      .single();

    if (data?.metadata && typeof data.metadata === 'object' && 'poll_responses' in data.metadata) {
      setResponses(data.metadata.poll_responses as { [key: string]: number });
    }
  };

  const handleVote = async (option: string) => {
    if (!user || userVote) return;
    
    setLoading(true);
    const newResponses = { ...responses };
    newResponses[option] = (newResponses[option] || 0) + 1;

    const { error } = await supabase
      .from('course_content')
      .update({
        metadata: { poll_responses: newResponses }
      })
      .eq('id', contentId);

    if (!error) {
      setResponses(newResponses);
      setUserVote(option);
      toast({ title: 'Vote recorded!', description: 'See how others voted' });
    }
    setLoading(false);
  };

  const totalVotes = Object.values(responses).reduce((a, b) => a + b, 0);

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          Live Poll
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="font-medium">{question}</p>
        
        {options.map((option) => {
          const votes = responses[option] || 0;
          const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
          const isSelected = userVote === option;

          return (
            <div key={option} className="space-y-2">
              <Button
                variant={isSelected ? 'default' : 'outline'}
                className="w-full justify-between"
                onClick={() => handleVote(option)}
                disabled={loading || !!userVote}
              >
                <span>{option}</span>
                {userVote && (
                  <Badge variant="secondary">{votes} votes</Badge>
                )}
              </Button>
              {userVote && (
                <Progress value={percentage} className="h-2" />
              )}
            </div>
          );
        })}

        {totalVotes > 0 && (
          <p className="text-sm text-muted-foreground text-center mt-4">
            {totalVotes} total {totalVotes === 1 ? 'response' : 'responses'}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

interface CalculatorProps {
  title: string;
  inputs: { label: string; id: string }[];
  formula: string;
  resultLabel?: string;
}

export const InteractiveCalculator: React.FC<CalculatorProps> = ({ title, inputs, formula, resultLabel = 'Result' }) => {
  const [values, setValues] = useState<{ [key: string]: number }>({});
  const [result, setResult] = useState<string | null>(null);

  const calculate = () => {
    try {
      const func = new Function(...Object.keys(values), formula);
      const res = func(...Object.values(values));
      setResult(res);
    } catch (error) {
      console.error('Calculation error:', error);
    }
  };

  return (
    <Card className="border-l-4 border-l-green-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {inputs.map((input) => (
          <div key={input.id}>
            <label className="text-sm font-medium">{input.label}</label>
            <Input
              type="number"
              value={values[input.id] || ''}
              onChange={(e) => setValues({ ...values, [input.id]: parseFloat(e.target.value) || 0 })}
              placeholder="Enter value"
            />
          </div>
        ))}
        
        <Button onClick={calculate} className="w-full">
          Calculate
        </Button>

        {result !== null && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border-2 border-green-500">
            <p className="text-sm text-muted-foreground">{resultLabel}</p>
            <p className="text-3xl font-bold text-green-700 dark:text-green-300">
              {result}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface WordCloudProps {
  contentId: string;
  prompt: string;
}

export const InteractiveWordCloud: React.FC<WordCloudProps> = ({ contentId, prompt }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [words, setWords] = useState<{ [key: string]: number }>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadWords();
  }, [contentId]);

  const loadWords = async () => {
    const { data } = await supabase
      .from('course_content')
      .select('metadata')
      .eq('id', contentId)
      .single();

    if (data?.metadata && typeof data.metadata === 'object' && 'wordcloud_data' in data.metadata) {
      setWords(data.metadata.wordcloud_data as { [key: string]: number });
    }
  };

  const handleSubmit = async () => {
    if (!user || !text.trim()) return;

    const newWords = { ...words };
    const textWords = text.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    
    textWords.forEach(word => {
      newWords[word] = (newWords[word] || 0) + 1;
    });

    const { error } = await supabase
      .from('course_content')
      .update({ metadata: { wordcloud_data: newWords } })
      .eq('id', contentId);

    if (!error) {
      setWords(newWords);
      setSubmitted(true);
      toast({ title: 'Response added!', description: 'See the word cloud update' });
    }
  };

  const maxCount = Math.max(...Object.values(words), 1);
  const sortedWords = Object.entries(words).sort((a, b) => b[1] - a[1]).slice(0, 50);

  return (
    <Card className="border-l-4 border-l-purple-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
          💭 Word Cloud
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="font-medium">{prompt}</p>

        {!submitted && (
          <div className="space-y-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your response..."
              className="w-full"
            />
            <Button onClick={handleSubmit} className="w-full">
              Submit Response
            </Button>
          </div>
        )}

        {sortedWords.length > 0 && (
          <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 rounded-lg flex flex-wrap gap-3 justify-center items-center min-h-[200px]">
            {sortedWords.map(([word, count]) => {
              const size = 12 + ((count / maxCount) * 32);
              return (
                <span
                  key={word}
                  className="font-bold text-purple-700 dark:text-purple-300 hover:scale-110 transition-transform"
                  style={{ fontSize: `${size}px` }}
                >
                  {word}
                </span>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface QuizProps {
  contentId: string;
  questions: Array<{
    question: string;
    options: string[];
    correctAnswer: number;
  }>;
}

export const InteractiveQuiz: React.FC<QuizProps> = ({ contentId, questions }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);

  const question = questions[currentQuestion];

  const handleSubmit = async () => {
    if (selected === null || !user) return;

    const isCorrect = selected === question.correctAnswer;
    setShowResult(true);

    if (isCorrect) {
      setScore(score + 10);
      toast({
        title: '🎉 Correct!',
        description: 'Great job! You earned 10 points.'
      });
    } else {
      toast({
        title: '❌ Incorrect',
        description: `The correct answer was: ${question.options[question.correctAnswer]}`,
        variant: 'destructive'
      });
    }
  };

  const handleNext = async () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelected(null);
      setShowResult(false);
    } else {
      setCompleted(true);
      
      // Update leaderboard
      const { data: existingData } = await supabase
        .from('course_content')
        .select('metadata')
        .eq('id', contentId)
        .single();

      const metadata = existingData?.metadata as any;
      const currentLeaderboard = metadata?.quiz_leaderboard || [];
      const userEntry = currentLeaderboard.find((e: any) => e.userId === user?.id);

      if (userEntry) {
        userEntry.score = Math.max(userEntry.score, score);
      } else {
        currentLeaderboard.push({
          userId: user?.id,
          userName: user?.email?.split('@')[0] || 'Anonymous',
          score: score,
          timestamp: new Date().toISOString()
        });
      }

      currentLeaderboard.sort((a: any, b: any) => b.score - a.score);

      await supabase
        .from('course_content')
        .update({ metadata: { ...metadata, quiz_leaderboard: currentLeaderboard } })
        .eq('id', contentId);

      setLeaderboard(currentLeaderboard);
    }
  };

  useEffect(() => {
    const loadLeaderboard = async () => {
      const { data } = await supabase
        .from('course_content')
        .select('metadata')
        .eq('id', contentId)
        .single();

      const metadata = data?.metadata as any;
      if (metadata?.quiz_leaderboard) {
        setLeaderboard(metadata.quiz_leaderboard);
      }
    };
    loadLeaderboard();
  }, [contentId]);

  if (completed) {
    return (
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-500" />
            Quiz Complete!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center p-6 bg-orange-50 dark:bg-orange-950 rounded-lg">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-orange-500" />
            <p className="text-2xl font-bold mb-2">Your Score: {score} / {questions.length * 10}</p>
            <p className="text-muted-foreground">
              {score === questions.length * 10 ? 'Perfect score! 🎉' : 
               score >= questions.length * 7 ? 'Great job! 👏' : 
               'Good effort! Keep learning! 📚'}
            </p>
          </div>

          {leaderboard.length > 0 && (
            <div className="p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Leaderboard
              </h4>
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((entry, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <Badge variant={index === 0 ? 'default' : 'secondary'}>
                        {index + 1}
                      </Badge>
                      {entry.userName}
                    </span>
                    <span className="font-bold">{entry.score} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button 
            onClick={() => {
              setCurrentQuestion(0);
              setScore(0);
              setSelected(null);
              setShowResult(false);
              setCompleted(false);
            }} 
            className="w-full"
          >
            Retake Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-orange-500">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-500" />
            Quiz Challenge
          </span>
          <Badge variant="secondary">
            Question {currentQuestion + 1} / {questions.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">Current Score: {score}</p>
        </div>

        <p className="font-medium text-lg">{question.question}</p>

        <div className="space-y-2">
          {question.options.map((option, index) => (
            <Button
              key={index}
              variant={selected === index ? 'default' : 'outline'}
              className="w-full justify-start"
              onClick={() => !showResult && setSelected(index)}
              disabled={showResult}
            >
              <span className="mr-2 font-bold">{String.fromCharCode(65 + index)}.</span>
              {option}
              {showResult && index === question.correctAnswer && ' ✓'}
            </Button>
          ))}
        </div>

        {!showResult && selected !== null && (
          <Button onClick={handleSubmit} className="w-full">
            Submit Answer
          </Button>
        )}

        {showResult && (
          <Button onClick={handleNext} className="w-full">
            {currentQuestion < questions.length - 1 ? 'Next Question' : 'View Results'}
          </Button>
        )}

        {leaderboard.length > 0 && (
          <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Leaderboard
            </h4>
            <div className="space-y-2">
              {leaderboard.slice(0, 5).map((entry, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    <Badge variant={index === 0 ? 'default' : 'secondary'}>
                      {index + 1}
                    </Badge>
                    {entry.userName}
                  </span>
                  <span className="font-bold">{entry.score} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
